import { useMutation } from "@tanstack/react-query";
import { IGenerateTransactionReportRequestModel } from "@lib/types";
import Clients from "core/api-clients";

interface GenerateReportResponse {
  id: string;
  url?: string;
}

interface IGenerateTransactionReportRequest
  extends IGenerateTransactionReportRequestModel {
  id?: string;
}

export function useTransactionReportDownload() {
  const POLL_INTERVAL = 5000; // 5 seconds
  const TIMEOUT = 60000; // 1 minute max polling time

  return useMutation<
    GenerateReportResponse,
    Error,
    IGenerateTransactionReportRequest
  >({
    mutationFn: async (request) => {
      let reportId = request.id;

      if (!reportId) {
        // Generate a new report if no ID is passed
        const response =
          await Clients.ManagementBackend.TransactionReports.generate(request);

        if (!response.id) {
          throw new Error("No ID received for the generated report.");
        }
        reportId = response.id;
      }

      return new Promise<GenerateReportResponse>(async (resolve, reject) => {
        const startTime = Date.now();

        const poll = async () => {
          try {
            const statusResponse =
              await Clients.ManagementBackend.TransactionReports.get({
                params: { id: reportId },
              });

            if (statusResponse.url) {
              const downloadReport = document.createElement("a");
              downloadReport.href = statusResponse.url;
              downloadReport.download = "downloaded_file";
              downloadReport.style.display = "none";
              document.body.appendChild(downloadReport);
              downloadReport.click();
              document.body.removeChild(downloadReport);
              resolve({ id: reportId as string, url: statusResponse.url });
              return;
            }

            if (Date.now() - startTime >= TIMEOUT) {
              reject(new Error("Report generation timed out."));
              return;
            }

            setTimeout(poll, POLL_INTERVAL);
          } catch (error) {
            reject(new Error("Failed to fetch report."));
          }
        };

        poll();
      });
    },
    onError: (error) => {
      console.error("Failed to generate or fetch report:", error);
    },
  });
}
